import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import Table from '../../UI/Table';
import { Text } from '../../UI/Typography';
import { getCompanyLeaves } from '../../../Services/Api/leaveService';

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: 12px;
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CompactTable = styled(Table)`
  th, td { padding: 10px !important; }
`;

const COLUMNS = [
  { key: 'full_name', label: 'Full Name' },
  { key: 'type', label: 'Type' },
  { key: 'start_date', label: 'Start' },
  { key: 'end_date', label: 'End' },
  { key: 'reason', label: 'Reason' },
  { key: 'status', label: 'Status' },
  { key: 'created_at', label: 'Created At' }
];

const LeavesEmptyState = () => (
  <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
    <Text size="lg" weight="medium" style={{ margin: '0 0 8px 0' }}>
      No Leave Records
    </Text>
    <Text size="sm" style={{ margin: 0, opacity: 0.7 }}>
      No leave requests found for this company.
    </Text>
  </div>
);

const formatDate = (d) => {
  if (!d) return 'N/A';
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return dt.toLocaleDateString();
  } catch {
    return d;
  }
};

const LeaveTable = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchLeaves = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const rows = await getCompanyLeaves();
      setData(rows || []);
    } catch (err) {
      setError(err.message || 'Failed to load leaves');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchLeaves(); }, [fetchLeaves]);

  const processed = useMemo(() => (
    (data || []).map(r => ({
      ...r,
      full_name: [r.first_name, r.last_name].filter(Boolean).join(' ') || r.username || 'N/A',
      start_date: formatDate(r.start_date),
      end_date: formatDate(r.end_date),
      created_at: formatDate(r.created_at)
    }))
  ), [data]);

  const renderCell = useCallback((item, column) => {
    const v = item[column.key];
    if (column.key === 'status') {
      const colorMap = { pending: 'orange', approved: 'green', rejected: 'red' };
      const color = colorMap[item.status] || '#6b7280';
      return <Badge style={{ border: `1px solid ${color}30`, color }}>{item.status}</Badge>;
    }
    return <Text size="sm" style={{ margin: 0 }}>{v ?? 'N/A'}</Text>;
  }, []);

  return (
    <>
      <CompactTable
        columns={COLUMNS}
        data={processed}
        renderCell={renderCell}
        isLoading={isLoading}
        error={error}
        emptyState={<LeavesEmptyState />}
        hoverable
      />
    </>
  );
};

export default LeaveTable;
